import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import routes from '../routes';
import ProtectedRoutes from './ProtectedRoutes';
import {
  CUSTOM_DASHBOARD_FLAG,
  DATA_MANAGEMENT_FLAG,
  DISABLE_ADVANCED_ANALYTICS,
} from '../config/env';

const Loader = React.lazy(() => import('../components/loader/Loader'));
const DataExtractorEmployees = React.lazy(() => import('../pages/DataExtractor/Employees'));
const DataExtractorPay = React.lazy(() => import('../pages/DataExtractor/Pay'));
const ForgotPassword = React.lazy(() => import('../pages/ForgotPassword'));
const HrQuestions = React.lazy(() => import('../pages/advancedAnalytics/HrQuestions'));
const GoogleAuthCallback = React.lazy(() => import('../pages/auth/GoogleAuthCallback'));
const MicrosoftAuthCallback = React.lazy(() => import('../pages/auth/MicrosoftAuthCallback'));
const SSOLogin = React.lazy(() => import('../pages/auth/SSOLogin'));
const MicrosoftSSOLogin = React.lazy(() => import('../pages/auth/MicrosoftSSOLogin'));
const MyDashboards = React.lazy(
  () => import('../pages/custom-dashboards/dashboards/my-dashboards/MyDashboardsView')
);
const DeepAnalytics = React.lazy(() => import('../pages/advancedAnalytics/deepAnalytics'));
const Dashboards = React.lazy(() => import('../pages/custom-dashboards/start/Index'));
const ViewDashboard = React.lazy(() => import('../pages/custom-dashboards/edit/Index'));
const DataQuality = React.lazy(() => import('../pages/data-management/data-quality/Index'));
const Login = React.lazy(() => import('../pages/login'));
const Overview = React.lazy(() => import('../pages/overview/Index'));
const SetPassword = React.lazy(() => import('../pages/setPassword'));
const Settings = React.lazy(() => import('../pages/settings'));
const ViewTemplates = React.lazy(
  () => import('../pages/custom-dashboards/templates/ViewTemplates')
);
const DataExplorer = React.lazy(() => import('../pages/custom-dashboards/DataExplorer'));
const Profile = React.lazy(() => import('../pages/settings/profile'));
const SharedWithMe = React.lazy(() => import('../pages/custom-dashboards/dashboards/sharedWithMe'));
const MyLibrary = React.lazy(() => import('../pages/library'));
const UpdateGraphConfigPage = React.lazy(() => import('../pages/custom-dashboards/edit/update'));
const GraphConfigPage = React.lazy(() => import('../pages/custom-dashboards/edit/create'));
const DataLexicon = React.lazy(() => import('../pages/data-management/data-lexicon/index'));
const MappingAndNormalization = React.lazy(
  () => import('../pages/data-management/mapping-normalization/index')
);
const Imports = React.lazy(() => import('../pages/data-management/imports/index'));
const EmployeeProfile = React.lazy(() => import('../pages/Employees/index'));
const OrganizationChart = React.lazy(() => import('../pages/DataExtractor/Organization'));
const PeopleHub = React.lazy(() => import('../pages/People-hub/index'));
const LegalHub = React.lazy(() => import('../pages/Legal-hub/index'));
const BDESEEditorPage = React.lazy(() => import('../pages/Legal-hub/BDESEEditorPage'));
const HomePage = React.lazy(() => import('../pages/home/index'));
const UpdateDomainPage = React.lazy(
  () => import('../pages/settings/domainsManagement/edit/UpdateDomainPage')
);
const ViewDomainPage = React.lazy(
  () => import('../pages/settings/domainsManagement/view/ViewDomainPage')
);
const CreateDomainPage = React.lazy(
  () => import('../pages/settings/domainsManagement/create/CreateDomainPage')
);
const DashboardExportPage = React.lazy(() => import('../pages/custom-dashboards/export'));
const IndexEgaliteHFPage = React.lazy(() => import('../pages/Legal-hub/IndexEgaliteHFPage'));

function AppRoutes() {
  const customDashboardFlag = CUSTOM_DASHBOARD_FLAG;
  const dataManagementFlag = DATA_MANAGEMENT_FLAG;
  const disableAdvancedAnalytics = DISABLE_ADVANCED_ANALYTICS;

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path={routes.DashboardExportPage(':dashboardId')}
          element={
            <ProtectedRoutes
              Component={DashboardExportPage}
              requiredPermission='Custom Dashboard'
            />
          }
        />
        <Route
          path={routes.LegalHubPage}
          element={<ProtectedRoutes Component={LegalHub} requiredPermission='BDESE' />}
        />
        <Route
          path={routes.IndexEgaliteHFPage()}
          element={<ProtectedRoutes Component={IndexEgaliteHFPage} requiredPermission='IndexMW' />}
        />
        <Route
          path={routes.BDESEViewPage(':reportId')}
          element={<ProtectedRoutes Component={BDESEEditorPage} requiredPermission='BDESE' />}
        />
        <Route exact path={routes.HomePage} element={<ProtectedRoutes Component={HomePage} />} />
        <Route
          exact
          path={routes.ViewDomainPage(':domainId')}
          element={<ProtectedRoutes Component={ViewDomainPage} />}
        />
        <Route
          exact
          path={routes.UpdateDomainPage(':domainId')}
          element={<ProtectedRoutes Component={UpdateDomainPage} requireSuperAdminOrDomainAdmin />}
        />
        <Route
          exact
          path={routes.CreateDomainPage}
          element={<ProtectedRoutes Component={CreateDomainPage} requireSuperAdmin />}
        />
        <Route exact path={routes.Overview} element={<ProtectedRoutes Component={Overview} />} />
        <Route path={routes.PeopleHubPage} element={<ProtectedRoutes Component={PeopleHub} />} />
        <Route
          path={routes.EmployeesPage}
          element={
            <ProtectedRoutes
              Component={DataExtractorEmployees}
              requiredPermission='PeopleHubEmployees'
              path='people'
            />
          }
        />
        <Route
          path={routes.DataExtractorPayPage}
          element={
            <ProtectedRoutes
              Component={DataExtractorPay}
              requiredPermission='PeopleHubPayroll'
              path='pay'
            />
          }
        />
        <Route
          path={routes.DataExtractorEmployeesPage(':employeeId')}
          element={
            <ProtectedRoutes
              Component={EmployeeProfile}
              requiredPermission='PeopleHubEmployeesProfile'
            />
          }
        />
        <Route
          path={routes.DataExtractorOrganizationPage}
          element={
            <ProtectedRoutes Component={OrganizationChart} requiredPermission='PeopleHubOrgChart' />
          }
        />
        {!disableAdvancedAnalytics && (
          <>
            <Route
              path={routes.AdvanceDeepAnalysisPage}
              element={
                <ProtectedRoutes
                  Component={DeepAnalytics}
                  requiredPermission='Advanced Analytics'
                />
              }
            />
            <Route
              path={routes.AdvancedAnalyticsPage}
              element={
                <ProtectedRoutes Component={HrQuestions} requiredPermission='Advanced Analytics' />
              }
            />
          </>
        )}
        <Route path={routes.SettingsPage} element={<ProtectedRoutes Component={Settings} />} />
        <Route path={routes.ProfilePage} element={<ProtectedRoutes Component={Profile} />} />

        {customDashboardFlag && (
          <>
            <Route
              path={routes.TemplatesPage}
              element={
                <ProtectedRoutes Component={Dashboards} requiredPermission='Custom Dashboard' />
              }
            />
            <Route
              path={routes.DashboardsSharedWithMePage}
              element={
                <ProtectedRoutes Component={SharedWithMe} requiredPermission='Custom Dashboard' />
              }
            />
            <Route
              path={routes.LibraryPage}
              element={
                <ProtectedRoutes Component={MyLibrary} requiredPermission='Custom Dashboard' />
              }
            />
            <Route
              path={routes.MyDashboardPage}
              element={
                <ProtectedRoutes Component={MyDashboards} requiredPermission='Custom Dashboard' />
              }
            />
            <Route
              path={routes.DashboardEditByIdPage(':dashboardId')}
              element={
                <ProtectedRoutes Component={ViewDashboard} requiredPermission='Custom Dashboard' />
              }
            />
            <Route
              path={routes.AddGraphPage(':dashboardId')}
              element={
                <ProtectedRoutes
                  Component={GraphConfigPage}
                  requiredPermission='Custom Dashboard'
                />
              }
            />
            <Route
              path={routes.GraphEditPage(':dashboardId', ':graphID')}
              element={
                <ProtectedRoutes
                  Component={UpdateGraphConfigPage}
                  requiredPermission='Custom Dashboard'
                />
              }
            />
            <Route
              path={routes.TemplateCategoryPage(':categoryId')}
              element={
                <ProtectedRoutes Component={ViewTemplates} requiredPermission='Custom Dashboard' />
              }
            />
            <Route
              path={routes.DashboardExplorerPage}
              element={
                <ProtectedRoutes Component={DataExplorer} requiredPermission='Custom Dashboard' />
              }
            />
          </>
        )}

        {dataManagementFlag && (
          <>
            <Route
              path={routes.DataQualityPage}
              element={
                <ProtectedRoutes
                  Component={DataQuality}
                  requiredPermission='DataManagementQuality'
                />
              }
            />
            <Route
              path={routes.DataLexiconPage}
              element={
                <ProtectedRoutes
                  Component={DataLexicon}
                  requiredPermission='DataManagementLexicon'
                />
              }
            />
            <Route
              path={routes.MappingAndNormalization}
              element={<ProtectedRoutes Component={MappingAndNormalization} />}
            />
            <Route
              path={routes.ImportsPage}
              element={
                <ProtectedRoutes Component={Imports} requiredPermission='DataManagementImports' />
              }
            />
          </>
        )}

        <Route path={routes.ForgotPasswordPage} element={<ForgotPassword />} />
        <Route path={routes.SetPasswordPage} element={<SetPassword />} />
        <Route path={routes.LoginPage} element={<Login />} />
        <Route path={routes.SSOLoginPage} element={<SSOLogin />} />
        <Route path={routes.GoogleCallbackPage} element={<GoogleAuthCallback />} />
        <Route path={routes.MicrosoftSSOLoginPage} element={<MicrosoftSSOLogin />} />
        <Route path={routes.MicrosoftCallbackPage} element={<MicrosoftAuthCallback />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
